import React, { useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Navigate } from 'react-router-dom';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import AdminOverview from '@/components/admin/AdminOverview';
import UsageMetricsDisplay from '@/components/admin/AdminUsageMetrics';
import AdminActivityStatsDisplay from '@/components/admin/AdminActivityStats';
import AdminFamilyAnalyticsDisplay from '@/components/admin/AdminFamilyAnalytics';
import { PageLayout } from '@/components/layout';

import { PushNotificationTester } from '@/components/admin/PushNotificationTester';
import { NotificationQueueMonitor } from '@/components/admin/NotificationQueueMonitor';
import { MealAnalysisTrigger } from '@/components/admin/MealAnalysisTrigger';
import { MealSuggestionTrigger } from '@/components/admin/MealSuggestionTrigger';
import { WellnessGenerationTrigger } from '@/components/admin/WellnessGenerationTrigger';
import { CronJobHistory } from '@/components/admin/CronJobHistory';
import { AccountCleanupMonitor } from '@/components/admin/AccountCleanupMonitor';
import { DataCoverageMonitor } from '@/components/admin/DataCoverageMonitor';
import { HeroImageDownloadTrigger } from '@/components/admin/HeroImageDownloadTrigger';
import StarterRecipesManager from '@/components/admin/StarterRecipesManager';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const AdminDashboard: React.FC = () => {
  const {
    user,
    isLoading: userLoading
  } = useUser();
  const {
    isAdmin,
    isLoading: adminLoading
  } = useAdminCheck();
  const [activeTab, setActiveTab] = useState('overview');

  const tabOptions = [
    { value: 'overview', label: 'Overview' },
    { value: 'usage', label: 'Usage Metrics' },
    { value: 'activity', label: 'Activity Stats' },
    { value: 'families', label: 'Family Analytics' },
    { value: 'push', label: 'Push Notifications' },
    { value: 'cleanup', label: 'Account Cleanup' },
    { value: 'analysis', label: 'Meal Data' },
    { value: 'coverage', label: 'Data Coverage' },
    { value: 'starters', label: 'Starter Recipes' }
  ];

  // Show loading while auth state or admin status is being determined
  if (userLoading || adminLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated or not admin
  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageLayout
      title="Admin Dashboard"
      description="Metrics and Tools"
    >
      {/* Navigation Select - below title */}
      <div className="mb-6">
        <Select value={activeTab} onValueChange={setActiveTab}>
          <SelectTrigger className="w-full sm:w-64 bg-background">
            <SelectValue placeholder="Select section" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            {tabOptions.map((tab) => (
              <SelectItem key={tab.value} value={tab.value}>
                {tab.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6 pb-14">
        <TabsContent value="overview" className="space-y-6 mt-0">
          <AdminOverview />
        </TabsContent>
        
        <TabsContent value="usage" className="space-y-6 mt-0">
          <UsageMetricsDisplay />
        </TabsContent>
        
        <TabsContent value="activity" className="space-y-6 mt-0">
          <AdminActivityStatsDisplay />
        </TabsContent>
        
        <TabsContent value="families" className="space-y-6 mt-0">
          <AdminFamilyAnalyticsDisplay />
        </TabsContent>
        
        <TabsContent value="push" className="space-y-6 mt-0">
          <NotificationQueueMonitor />
          <PushNotificationTester />
        </TabsContent>
        
        <TabsContent value="cleanup" className="space-y-6 mt-0">
          <AccountCleanupMonitor />
        </TabsContent>
        
        <TabsContent value="analysis" className="space-y-6 mt-0">
          <CronJobHistory />
          <MealAnalysisTrigger />
          <MealSuggestionTrigger />
          <WellnessGenerationTrigger />
        </TabsContent>
        
        <TabsContent value="coverage" className="space-y-6 mt-0">
          <DataCoverageMonitor />
          <HeroImageDownloadTrigger />
        </TabsContent>
        
        <TabsContent value="starters" className="space-y-6 mt-0">
          <StarterRecipesManager />
        </TabsContent>
      </Tabs>
    </PageLayout>
  );
};

export default AdminDashboard;
